// @ts-strict-ignore
import { Inject, Injectable, InjectionToken } from '@angular/core';
import { datadogLogs, Logger, LogsEvent, LogsInitConfiguration } from '@datadog/browser-logs';

import { DevModeService } from 'src/app/core/dev/dev-mode/dev-mode.service';
import { Environment } from 'src/configs/environment/environment.interface';
import { environmentRef } from 'src/configs/environment/environment.token';

import { LogMethod } from '../logger/log-method.enum';
import { LoggerService } from '../logger/logger.service';

const DATADOG_CLIENT_TOKEN = 'pub42046631117281d59c57892c5bb44d7f';
const DATADOG_SERVICE_NAME = 'dh-io-owcc-fe';
const DATADOG_TENANT = 'owcc';

export const dataDogInitOptions = {
  clientToken: DATADOG_CLIENT_TOKEN,
  site: 'datadoghq.eu',
  service: DATADOG_SERVICE_NAME,
  sampleRate: 100 // 100% of logs are sent,
  // silentMultipleInit: true
};

export const DATADOG_INIT_OPTIONS = new InjectionToken<LogsInitConfiguration>('dataDogInitOptions', {
  providedIn: 'root',
  factory: () => dataDogInitOptions
});

@Injectable({
  providedIn: 'root'
})
export class DatadogService {
  private datadogLogger: Logger;

  public constructor(
    private readonly logger: LoggerService,
    private readonly devModeService: DevModeService,
    @Inject(DATADOG_INIT_OPTIONS) private readonly initOptions: LogsInitConfiguration,
    @Inject(environmentRef) private readonly environment: Environment
  ) {}

  public init(): void {
    datadogLogs.init({
      ...this.initOptions,
      forwardErrorsToLogs: this.devModeService.isDevMode,
      beforeSend: this.datadogBeforeSend
    });

    datadogLogs.addLoggerGlobalContext('tenant', DATADOG_TENANT);

    this.datadogLogger = datadogLogs.logger;
    this.datadogLogger.setHandler('http');
  }

  public info(message: string, context?: object): void {
    this.datadogLog(message, LogMethod.InfoMessage, context);
  }

  public warn(message: string, context?: object): void {
    this.datadogLog(message, LogMethod.WarnMessage, context);
  }

  // Callback to process messages before sending them to datadog. Extend as neccessary
  private readonly datadogBeforeSend = (log: LogsEvent): void | boolean => {
    if (!this.environment.production) {
      return false;
    }

    // Remove unneccessary datadog specific prefix for general error messages
    log.message = log.message.replace('console error: ', '');

    // Remove color prefixes
    log.message = log.message.replace(/%c(\[.*]) color: [A-Za-z-]+\s/, '$1 ');
  };

  // Handles manual logging for info and warnings as errors are automatically logged
  private datadogLog(message: string, method: LogMethod, context?: object): void {
    if (!!this.environment.production && [LogMethod.InfoMessage, LogMethod.WarnMessage].includes(method)) {
      this.datadogLogger[method](message, context ?? undefined);
      this.logger[method](message, context ?? undefined);
    }
  }
}
